import React, { useState, useEffect } from "react";
import { Outlet, NavLink, Link, useNavigate } from "react-router-dom";
import {
  ShieldCheck, LayoutDashboard, FileText, FlaskConical, Radar,
  FileBarChart2, Settings, LogOut, Bell, Menu, X, ChevronDown, Crown, CreditCard,
  Activity, BookOpen, GitCompare, Users,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/documents", label: "Documents", icon: FileText },
  { to: "/scans", label: "Scans", icon: FlaskConical },
  { to: "/compare", label: "Compare", icon: GitCompare },
  { to: "/templates", label: "Templates", icon: BookOpen },
  { to: "/monitoring", label: "Monitoring", icon: Radar },
  { to: "/reports", label: "Reports", icon: FileBarChart2 },
  { to: "/recipients", label: "Recipients", icon: Users },
  { to: "/billing", label: "Billing", icon: CreditCard },
  { to: "/settings", label: "Settings", icon: Settings },
  { to: "/settings/notifications", label: "Notifications", icon: Bell },
];

const ADMIN_NAV = [
  { to: "/admin/leads", label: "Admin · Leads", icon: Crown },
  { to: "/admin/analytics", label: "Admin · AI Usage", icon: Activity },
];

export default function AppShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [workspace, setWorkspace] = useState(null);
  const [notifications, setNotifications] = useState([]);

  useEffect(() => {
    api.get("/workspaces/current").then((r) => setWorkspace(r.data)).catch(() => {});
    api.get("/notifications").then((r) => setNotifications(r.data || [])).catch(() => {});
  }, []);

  const unread = notifications.filter((n) => !n.read).length;

  const onLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen flex bg-slate-50 text-slate-900">
      {/* Sidebar */}
      <aside
        className={
          "fixed lg:static inset-y-0 left-0 z-40 w-64 bg-white border-r border-slate-200 " +
          "transform transition-transform lg:translate-x-0 " +
          (sidebarOpen ? "translate-x-0" : "-translate-x-full")
        }
        data-testid="app-sidebar"
      >
        <div className="h-16 flex items-center px-5 border-b border-slate-200">
          <Link to="/dashboard" className="flex items-center gap-2">
            <span className="h-8 w-8 rounded-md bg-blue-600 inline-flex items-center justify-center text-white">
              <ShieldCheck className="h-4 w-4" strokeWidth={2.5} />
            </span>
            <span className="font-display font-semibold text-lg text-slate-900">
              AgentFlow<span className="text-blue-600">.</span>
            </span>
          </Link>
        </div>

        <div className="px-3 py-4">
          <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5">
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">Workspace</div>
            <div className="mt-0.5 text-sm font-semibold text-slate-900 truncate" data-testid="workspace-name">
              {workspace?.name || "Loading…"}
            </div>
            <div className="mt-1 inline-flex items-center gap-1 rounded-full bg-blue-100 text-blue-700 text-[10px] uppercase tracking-wide font-semibold px-2 py-0.5">
              {workspace?.plan || "free"} plan
            </div>
          </div>
        </div>

        <nav className="px-3 pb-6 space-y-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              onClick={() => setSidebarOpen(false)}
              data-testid={`nav-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                "flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors " +
                (isActive
                  ? "bg-blue-50 text-blue-700"
                  : "text-slate-600 hover:bg-slate-100 hover:text-slate-900")
              }
            >
              <n.icon className="h-4 w-4" />
              {n.label}
            </NavLink>
          ))}

          {user?.role === "admin" && (
            <>
              <div className="pt-4 mt-2 border-t border-slate-100">
                <div className="px-3 pb-1.5 text-[10px] uppercase tracking-[0.18em] text-slate-400 font-semibold">
                  Administration
                </div>
              </div>
              {ADMIN_NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  onClick={() => setSidebarOpen(false)}
                  data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                  className={({ isActive }) =>
                    "flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors " +
                    (isActive
                      ? "bg-amber-50 text-amber-800"
                      : "text-slate-600 hover:bg-slate-100 hover:text-slate-900")
                  }
                >
                  <n.icon className="h-4 w-4" />
                  {n.label}
                </NavLink>
              ))}
            </>
          )}
        </nav>

        <div className="px-4 pb-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-slate-400" data-testid="sidebar-legal-links">
          <Link to="/terms" className="hover:text-slate-600 transition">Terms</Link>
          <Link to="/privacy" className="hover:text-slate-600 transition">Privacy</Link>
          <Link to="/refund" className="hover:text-slate-600 transition">Refunds</Link>
        </div>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-200 bg-white">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="w-full flex items-center gap-3 rounded-lg px-2 py-2 hover:bg-slate-100 transition-colors text-left"
                data-testid="user-menu-trigger"
              >
                <div className="h-8 w-8 rounded-full bg-blue-100 text-blue-700 inline-flex items-center justify-center font-semibold">
                  {(user?.name || user?.email || "?").slice(0, 1).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-900 truncate">{user?.name || "User"}</div>
                  <div className="text-xs text-slate-500 truncate">{user?.email}</div>
                </div>
                <ChevronDown className="h-4 w-4 text-slate-400" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>{user?.email}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate("/settings")}>
                <Settings className="h-4 w-4 mr-2" /> Settings
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onLogout} data-testid="logout-menu-item">
                <LogOut className="h-4 w-4 mr-2" /> Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-slate-900/40 z-30"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <div className="flex-1 min-w-0 flex flex-col">
        <header className="sticky top-0 z-20 h-16 bg-white/80 backdrop-blur border-b border-slate-200 flex items-center px-4 sm:px-6 gap-3">
          <button
            className="lg:hidden h-9 w-9 rounded-md hover:bg-slate-100 inline-flex items-center justify-center"
            onClick={() => setSidebarOpen((v) => !v)}
            data-testid="mobile-sidebar-toggle"
          >
            {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
          <div className="flex-1" />
          <NotificationsBell
            notifications={notifications}
            setNotifications={setNotifications}
            unread={unread}
          />
        </header>

        <main className="flex-1 px-4 sm:px-6 lg:px-10 py-8 max-w-7xl w-full mx-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function NotificationsBell({ notifications, setNotifications, unread }) {
  const markAllRead = async () => {
    try {
      await api.post("/notifications/read-all");
      setNotifications((ns) => ns.map((n) => ({ ...n, read: true })));
    } catch {/* ignore */}
  };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="relative h-9 w-9 rounded-md hover:bg-slate-100 inline-flex items-center justify-center"
          data-testid="notifications-trigger"
        >
          <Bell className="h-5 w-5 text-slate-600" />
          {unread > 0 && (
            <span className="absolute top-1.5 right-1.5 h-2 w-2 rounded-full bg-rose-500" />
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80">
        <div className="flex items-center justify-between px-3 py-2">
          <DropdownMenuLabel className="p-0">Notifications</DropdownMenuLabel>
          <button onClick={markAllRead} className="text-xs text-blue-600 hover:text-blue-700 font-medium">
            Mark all read
          </button>
        </div>
        <DropdownMenuSeparator />
        {notifications.length === 0 && (
          <div className="px-4 py-6 text-sm text-slate-500">No notifications yet.</div>
        )}
        <div className="max-h-96 overflow-auto">
          {notifications.slice(0, 12).map((n) => (
            <div
              key={n.id}
              className={"px-3 py-2.5 border-b border-slate-100 last:border-b-0 " + (n.read ? "" : "bg-blue-50/40")}
              data-testid={`notification-${n.id}`}
            >
              <div className="text-sm font-medium text-slate-900">{n.title}</div>
              <div className="text-xs text-slate-600 mt-0.5">{n.body}</div>
            </div>
          ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
